import { apolloClient } from '~/common/apollo'
import { action, computed, observable, runInAction } from '~/common/mobx.decorator'
import { uniqBy } from 'lodash'

export const COLLECTION_LIMIT = 10

class CollectionCursorStore {
	@observable isReady = false
	@observable list = []
	@observable pageInfo = {
		totalCount: 0,
		endCursor: '',
		isLoading: false,
		hasNextPage: false,
	}
	@observable customData = {}
	@observable customDataKeys = []
	@observable dataKey = ''
	@observable sortBy = { field: 'NEWEST', order: 'desc' }

	constructor(query, dataKey, customDataKeys) {
		this.query = query
		this.dataKey = dataKey
		if (customDataKeys) {
			this.customDataKeys = customDataKeys
		}
	}

	@computed get isLoading() {
		return !!this.pageInfo.isLoading
	}

	@computed get totalItem() {
		return this.pageInfo.totalCount
	}

	@computed get isEmpty() {
		return !this.list.length
	}

	@computed get canLoadMore() {
		return this.pageInfo.hasNextPage
	}

	@action setCustomDataKeys(keys) {
		this.customDataKeys = keys
	}

	@action
	setSortBy(sortBy) {
		this.sortBy = sortBy
	}

	@action
	load = async (isReload = false, params = {}, forceLoad = false) => {
		if (this.pageInfo?.isLoading || (!isReload && !this.canLoadMore && !forceLoad)) {
			return {}
		}
		this.pageInfo.isLoading = true

		if (isReload) {
			this.pageInfo.endCursor = ''
			this.pageInfo.hasNextPage = true
		}

		const response = await apolloClient.query({
			query: this.query,
			variables: {
				first: COLLECTION_LIMIT,
				after: params.after || this.pageInfo.endCursor,
				...params,
				sortBy: this.sortBy,
			},
		})

		const result = response?.data[this.dataKey]
		if (result) {
			const data = result.nodes
			const { pageInfo } = result

			runInAction(() => {
				this.pageInfo = { ...pageInfo, totalCount: result.totalCount ?? 0 }

				if (data) {
					this.list = isReload ? [...data] : [...this.list, ...data]
					this.isReady = true
				}
				if (result && this.customDataKeys.length) {
					this.customDataKeys.forEach((key) => {
						if (Object.keys(result).includes(key)) {
							this.customData[key] = result[key]
						}
					})
				}
			})
		}

		runInAction(() => {
			this.pageInfo.isLoading = false
		})

		return response
	}

	@action
	updateItem(item) {
		const index = this.list.findIndex((x) => x.id === item.id)

		if (index > -1) {
			const newList = [...this.list]
			newList[index] = { ...newList[index], ...item }
			this.list = newList
		}
	}

	@action
	updateItemByIndex(index, item) {
		const newList = [...this.list]
		newList[index] = { ...newList[index], ...item }
		this.list = newList
	}

	@action
	removeItem(id) {
		const index = this.list.findIndex((x) => x.id === id)

		if (index > -1) {
			this.list = [...this.list.slice(0, index), ...this.list.slice(index + 1)]
			this.pageInfo.totalCount = this.pageInfo.totalCount != null ? this.pageInfo.totalCount - 1 : 0
		}
	}

	@action
	addNewItem(item) {
		this.list = uniqBy([item, ...this.list], 'id')
		this.pageInfo.totalCount = this.pageInfo.totalCount != null ? this.pageInfo.totalCount + 1 : 1
	}

	@action
	addNewMultiItems(items) {
		this.list = uniqBy([...items, ...this.list], 'id')
		this.pageInfo.totalCount = this.pageInfo.totalCount != null ? this.pageInfo.totalCount + items.length : items.length
	}

	getCustomDataByKey(key) {
		return this.customData[key]
	}

	@action
	setReady() {
		this.isReady = true
	}

	@action
	reset() {
		this.list = []
		this.pageInfo = {
			totalCount: 0,
			endCursor: '',
			isLoading: false,
			hasNextPage: false,
		}
		this.isReady = false
		this.customData = {}
	}

	@action
	resetFetchedData = () => {
		this.list = []
		this.pageInfo = {
			totalCount: 0,
			endCursor: '',
			isLoading: false,
			hasNextPage: true,
		}
		this.isReady = false
		this.customData = {}
	}
}

export { CollectionCursorStore }
